import {
  List,
  ListItem,
  Stack,
  Table,
  TableHead,
  Typography,
} from "@mui/material";
import { DiseaseInfo } from "../../commons";
import {
  BasisOfDetermination,
  InterviewTableBody,
  Question,
  QuestionRows,
  SectionTitle,
  TableContainer,
  TableHeaderCell,
  TableQuestionTypography,
  TableRow,
} from "./commons";

export interface SASRiskContent2Props {
  diseaseInfo: DiseaseInfo;
  answeredDate: string;
}

export default function SASRiskContent2({
  diseaseInfo,
  answeredDate,
}: SASRiskContent2Props) {
  return (
    <>
      <Stack sx={{ gap: "24px" }}>
        <SectionTitle text="STOP-BANG尺度の結果" />
        <StopBangResult diseaseInfo={diseaseInfo} />
      </Stack>
      <Stack sx={{ gap: "24px" }}>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <SectionTitle text="参考情報：STOP-BANG尺度の回答内容" />
          <Typography sx={{ fontSize: "10px", fontWeight: 400 }}>
            回答日 : {answeredDate}
          </Typography>
        </Stack>
        <StopBangAnswers diseaseInfo={diseaseInfo} />
      </Stack>
    </>
  );
}

interface StopBangResultProps {
  diseaseInfo: DiseaseInfo;
}
function StopBangResult({ diseaseInfo }: StopBangResultProps) {
  const judgementListItems = [
    ["STOP-BANG尺度0-2点 :", "正常"],
    ["STOP-BANG尺度3-8点 :", "睡眠時無呼吸症候群のリスクあり"],
    ["---", "回答数不足のため判定不能"],
  ];
  return (
    <Stack direction="row" sx={{ ml: "8px" }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "center",
          alignItems: "baseline",
          gap: "8px",
          border: "1px solid #000",
          height: "98px",
          width: "238px",
        }}
      >
        <Typography sx={{ fontSize: "40px", fontWeight: 700, mt: "12px" }}>
          {diseaseInfo.score}
        </Typography>
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          点/ {diseaseInfo.max_score}点
        </Typography>
      </Stack>
      <Stack sx={{ ml: "18px" }}>
        <List sx={{ gap: "10px", p: 0 }}>
          {judgementListItems.map((item, idx) => (
            <ListItem
              key={idx}
              sx={{
                p: 0,
                "& .MuiTypography-root": { fontSize: "10px", fontWeight: 400 },
              }}
            >
              <Typography component="span" sx={{ width: "120px" }}>
                {item[0]}
              </Typography>
              <Typography component="span" sx={{ ml: "4px" }}>
                {item[1]}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Stack>
    </Stack>
  );
}

interface StopBangAnswersProps {
  diseaseInfo: DiseaseInfo;
}

function StopBangAnswers({ diseaseInfo }: StopBangAnswersProps) {
  const questions: Question[] = [
    /* eslint-disable react/jsx-key */
    <>
      <TableQuestionTypography>大きないびきをかく</TableQuestionTypography>
      <Typography
        sx={{
          color: "#000000",
          fontFeatureSettings: "'clig' off, 'liga' off",
          fontFamily: "Noto Sans JP",
          fontSize: "8px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "150%",
          letterSpacing: "0.2px",
        }}
      >
        ※話し声よりも大きいか、あるいは閉めた扉越しに聞こえる程度。
      </Typography>
    </>,
    <TableQuestionTypography>
      日中にしばしば疲労感または眠気を感じる
    </TableQuestionTypography>,
    <TableQuestionTypography>
      他の人から呼吸が睡眠中に停止しているのを指摘されている
    </TableQuestionTypography>,
    <TableQuestionTypography>
      高血圧、あるいは現在高血圧の治療を受けている
    </TableQuestionTypography>,
    <TableQuestionTypography>BMIが30以上</TableQuestionTypography>,
    <TableQuestionTypography>50歳以上</TableQuestionTypography>,
    <TableQuestionTypography>首の周囲径が40cm以上</TableQuestionTypography>,
    <TableQuestionTypography>性別が男</TableQuestionTypography>,
    /* eslint-enable react/jsx-key */
  ].map((q) => ({
    question: q,
    choices: [
      {
        text: "いいえ",
        point: 0,
      },
      {
        text: "はい",
        point: 1,
      },
    ],
  }));
  const cellBorderStyle = {
    border: "solid #000",
    borderWidth: "0 1px 1px 0",
  };
  const headerCellHeight = "34px";
  return (
    <Stack>
      <TableContainer
        sx={{
          ml: "8px",
          borderRadius: 0,
          border: "solid #000",
          borderWidth: "1px 0 0 1px",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={cellBorderStyle}>
              <TableHeaderCell
                height={headerCellHeight}
                width="306px"
                isFirstCell={true}
              >
                質問
              </TableHeaderCell>
              <TableHeaderCell height={headerCellHeight} width="55px">
                回答
              </TableHeaderCell>
              <TableHeaderCell height={headerCellHeight} width="318px">
                選択肢
              </TableHeaderCell>
              <TableHeaderCell height={headerCellHeight} width="50px">
                点数
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <InterviewTableBody>
            {questions.map((q, i) => (
              <QuestionRows
                key={i}
                question={q}
                rowIndex={i}
                choiceIndex={diseaseInfo.choice_indices[i]}
                rowHeight="35px"
                choiceStyle={{
                  color: "#000",
                  fontFeatureSettings: "'clig' off, 'liga' off",
                  fontFamily: "Noto Sans JP",
                  fontSize: "11px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "150%",
                  letterSpacing: "0.2px",
                }}
                pointStyle={{
                  color: "#000",
                  fontFeatureSettings: "'clig' off, 'liga' off",
                  fontFamily: "Noto Sans JP",
                  fontSize: "11px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "150%",
                  letterSpacing: "0.2px",
                }}
              />
            ))}
          </InterviewTableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" sx={{ m: "4px 8px 0 8px" }}>
        <BasisOfDetermination
          basisAnnotations={[
            "睡眠時無呼吸症候群（SAS）の診療ガイドライン 2020 （日本呼吸器学会）",
          ]}
        />
      </Stack>
    </Stack>
  );
}
